import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';
import { FieldArray } from 'formik';
import {
  Form,
  InputPlaceholder,
  TextareaPlaceholder,
  CheckboxGroupPlaceholder,
  H4,
  H6,
  A,
  Row,
  SectionDivider,
  ActionsWrapper
} from '@contently/toolkit';
import ContentStrategySection from '../forms/content_strategy_section';
import DirectAttachmentUpload, {
  useDirectAttachmentUploadFormik
} from '../forms/active_storage_direct_uploader';
import AnswerField from '../questions/AnswerField';

const ContentRequestForm = props => {
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    isSubmitting,
    isFetching,
    setFieldValue,
    contentRequestForm
  } = props;

  const [isUploading, uploaderProps] = useDirectAttachmentUploadFormik(props);

  function renderQuestions() {
    if (!(values.questions && values.questions.length)) return <Fragment />;

    return values.questions.map((question, index) => (
      <AnswerField
        question={question}
        handleBlur={handleBlur}
        handleChange={handleChange}
        errors={errors.questions}
        index={index}
        key={question.label}
      />
    ));
  }

  return (
    <form onSubmit={handleSubmit} data-parsley-excluded="true">
      <SectionDivider>
        <H4>Project Details</H4>

        <Row>
          <ReactPlaceholder
            showLoadingAnimation
            customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
            ready={!isFetching}
          >
            <Form.Label required htmlFor="projectTitle">
              Title
            </Form.Label>
            <Form.ErrorWrapper touched={touched.projectTitle} error={errors.projectTitle}>
              <Form.Input
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.projectTitle}
                name="projectTitle"
                id="projectTitle"
                placeholder="Give your content request form a name"
                required
              />
            </Form.ErrorWrapper>
          </ReactPlaceholder>
        </Row>

        <Row>
          <ReactPlaceholder
            showLoadingAnimation
            customPlaceholder={<TextareaPlaceholder wrapperClass="row" withLabel />}
            ready={!isFetching}
          >
            <Form.Label htmlFor="description">Description</Form.Label>
            <Form.ErrorWrapper touched={touched.description} error={errors.description}>
              <Form.MarkdownPreviewTextarea
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.description}
                name="description"
                id="description"
                placeholder="Describe the project"
                outdated
              />
            </Form.ErrorWrapper>
          </ReactPlaceholder>
        </Row>

        <Row>
          <div className="sub-interaction">
            <ReactPlaceholder
              showLoadingAnimation
              customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
              ready={!isFetching}
            >
              <Form.Label htmlFor="dueDate">Due date</Form.Label>
              <Form.ErrorWrapper touched={touched.dueDate} error={errors.dueDate}>
                <Form.DateTimePicker
                  type="datetime"
                  onChange={value => {
                    setFieldValue('dueDate', value);
                  }}
                  onBlur={handleBlur}
                  selected={values.dueDate}
                  name="dueDate"
                  id="dueDate"
                  placeholder="Select a date"
                />
              </Form.ErrorWrapper>
            </ReactPlaceholder>
          </div>
        </Row>

        {(contentRequestForm.includePillar || contentRequestForm.includeAudience) && (
          <ContentStrategySection
            {...props}
            fetching={isFetching}
            brandProfileId={contentRequestForm.brandProfile.id}
            audiences={contentRequestForm.audiences}
            pillars={contentRequestForm.pillars}
          />
        )}

        <Row>
          <ReactPlaceholder
            showLoadingAnimation
            customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
            ready={!isFetching}
          >
            <H6>Additional Materials</H6>
            <Form.ErrorWrapper touched error={errors.file}>
              <DirectAttachmentUpload {...uploaderProps} />
            </Form.ErrorWrapper>
          </ReactPlaceholder>
        </Row>

        <Row>
          <ReactPlaceholder
            showLoadingAnimation
            customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
            ready={!isFetching}
          >
            <Form.Label htmlFor="department">Department</Form.Label>
            <Form.ErrorWrapper touched={touched.department} error={errors.department}>
              <Form.Input
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.department}
                name="department"
                id="department"
                placeholder="Enter your department..."
              />
            </Form.ErrorWrapper>
          </ReactPlaceholder>
        </Row>

        <FieldArray name="questions" render={renderQuestions} />
      </SectionDivider>

      <SectionDivider>
        <H4>Contact Information</H4>

        <Row>
          <div className="interaction">
            <div className="sub-interaction">
              <ReactPlaceholder
                showLoadingAnimation
                customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
                ready={!isFetching}
              >
                <Form.Label required htmlFor="externalUser.firstName">
                  First name
                </Form.Label>
                <Form.ErrorWrapper
                  error={errors.externalUser && errors.externalUser.firstName}
                  touched={touched.externalUser && touched.externalUser.firstName}
                >
                  <Form.Input
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.externalUser.firstName}
                    name="externalUser.firstName"
                    id="externalUser.firstName"
                    placeholder="First name"
                    required
                  />
                </Form.ErrorWrapper>
              </ReactPlaceholder>
            </div>
            <div className="sub-interaction">
              <ReactPlaceholder
                showLoadingAnimation
                customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
                ready={!isFetching}
              >
                <Form.Label required htmlFor="externalUser.lastName">
                  Last name
                </Form.Label>
                <Form.ErrorWrapper
                  error={errors.externalUser && errors.externalUser.lastName}
                  touched={touched.externalUser && touched.externalUser.lastName}
                >
                  <Form.Input
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.externalUser.lastName}
                    name="externalUser.lastName"
                    id="externalUser.lastName"
                    placeholder="Last name"
                    required
                  />
                </Form.ErrorWrapper>
              </ReactPlaceholder>
            </div>
          </div>
        </Row>

        <Row>
          <ReactPlaceholder
            showLoadingAnimation
            customPlaceholder={<InputPlaceholder wrapperClass="row" withLabel />}
            ready={!isFetching}
          >
            <Form.Label required htmlFor="externalUser.email">
              Email
            </Form.Label>
            <Form.ErrorWrapper
              error={errors.externalUser && errors.externalUser.email}
              touched={touched.externalUser && touched.externalUser.email}
            >
              <Form.Input
                type="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.externalUser.email}
                name="externalUser.email"
                id="externalUser.email"
                placeholder="Email"
                required
              />
            </Form.ErrorWrapper>
          </ReactPlaceholder>
        </Row>

        <Row>
          <ReactPlaceholder
            showLoadingAnimation
            customPlaceholder={<CheckboxGroupPlaceholder withLabel checkboxes={1} />}
            ready={!isFetching}
          >
            <Form.Checkbox
              id="remember"
              label="Remember contact information"
              value="remember"
              checked={values.remember}
              onChange={value => {
                setFieldValue('remember', value);
              }}
            />
          </ReactPlaceholder>
        </Row>
      </SectionDivider>

      <SectionDivider>
        <Row>
          <ReactPlaceholder
            showLoadingAnimation
            customPlaceholder={<CheckboxGroupPlaceholder withLabel checkboxes={1} />}
            ready={!isFetching}
          >
            <Form.ErrorWrapper error={errors.terms} touched={touched.terms}>
              <Form.Checkbox
                id="terms"
                label={
                  <Fragment>
                    I agree to Contently&apos;s{' '}
                    <A href="https://contently.com/terms/" title="Terms of Service">
                      Terms of Service
                    </A>{' '}
                    and{' '}
                    <A href="https://contently.com/privacy-policy/" title="Privacy Policy">
                      Privacy Policy
                    </A>
                    .
                  </Fragment>
                }
                value="terms"
                checked={values.terms}
                onChange={value => {
                  setFieldValue('terms', value);
                }}
                required
              />
            </Form.ErrorWrapper>
          </ReactPlaceholder>
        </Row>
      </SectionDivider>

      <ActionsWrapper>
        <Form.ButtonWithProgress
          type="submit"
          disabled={isSubmitting || isUploading}
          variant="primary"
        >
          Submit Request
        </Form.ButtonWithProgress>
      </ActionsWrapper>
    </form>
  );
};

ContentRequestForm.defaultProps = {
  isFetching: false,
  isSubmitting: false,
  streamingData: null
};

ContentRequestForm.propTypes = {
  streamingData: PropTypes.object,
  contentRequestForm: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool,
  isFetching: PropTypes.bool
};

export default ContentRequestForm;



// WEBPACK FOOTER //
// ./app/javascript/app/components/content_requests/form.jsx